import { access, stat } from "node:fs/promises";

/**
 * Result of awaiting the entrypoint's background git preparation.
 *
 * - `"ready"`     — the ready marker appeared; the task repo is up to date.
 * - `"failed"`    — the failed marker appeared; git preparation errored (stale
 *                   image, fetch/checkout/clone failure). The caller must NOT
 *                   operate on the repo.
 * - `"timeout"`   — neither marker appeared before the deadline.
 * - `"not-gated"` — no marker path is configured (GitHub Codespaces / local),
 *                   so this environment does not gate on the bash git block.
 */
export type GitReadyState = "ready" | "failed" | "timeout" | "not-gated";

export interface AwaitGitReadyOptions {
  /** Path to the ready marker. Defaults to `CONVEYOR_GIT_READY_MARKER` env. */
  markerPath?: string;
  /** Path to the failed marker. Defaults to `CONVEYOR_GIT_FAILED_MARKER` env
   *  then `/workspaces/.conveyor-git-failed`. */
  failedPath?: string;
  /** Overall deadline. Defaults to ~10m to match the entrypoint's historical
   *  fail-loud provision window. */
  timeoutMs?: number;
  /** Poll interval. Defaults to 200ms. */
  pollMs?: number;
  /** Optional log sink for progress messages. */
  onLog?: (msg: string) => void;
  /** Optional shutdown signal. Aborting rejects with AbortError. */
  signal?: AbortSignal;
}

const DEFAULT_FAILED_PATH = "/workspaces/.conveyor-git-failed";
const DEFAULT_TIMEOUT_MS = 600_000;
const DEFAULT_POLL_MS = 200;

async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    // A directory would never be our marker file; stat guards against a stale
    // dir of the same name from a prior pod on the baked image.
    const s = await stat(path);
    return s.isFile();
  } catch {
    return false;
  }
}

/**
 * Block until the pod entrypoint's backgrounded git preparation writes either
 * the ready or the failed marker.
 *
 * The v3 pod entrypoint launches `conveyor-agent` immediately (so the card
 * lights up) while `prepare_workspace_git` runs in the background. It exports
 * `CONVEYOR_GIT_READY_MARKER` and writes exactly one of the ready/failed
 * markers on completion. This helper is the agent-side gate: Claude must not
 * spawn on a stale branch, and setup/start scripts need the repo present.
 *
 * When no marker path is configured (GitHub Codespaces / local), returns
 * `"not-gated"` immediately so those environments keep their own git-sync
 * fallback (session-runner phase 3.5, guarded by `CONVEYOR_GIT_READY !== "1"`).
 *
 * Without a signal, never throws and always resolves to a `GitReadyState`.
 * An explicit shutdown abort rejects promptly with `AbortError`.
 */
export function awaitGitReady(opts: AwaitGitReadyOptions = {}): Promise<GitReadyState> {
  if (opts.signal?.aborted) return Promise.reject(abortError());
  const markerPath = opts.markerPath ?? process.env.CONVEYOR_GIT_READY_MARKER;
  if (!markerPath) {
    return Promise.resolve("not-gated");
  }
  const failedPath =
    opts.failedPath ?? process.env.CONVEYOR_GIT_FAILED_MARKER ?? DEFAULT_FAILED_PATH;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const pollMs = opts.pollMs ?? DEFAULT_POLL_MS;

  opts.onLog?.(`waiting for workspace git (marker=${markerPath})`);
  return pollForMarkers(markerPath, failedPath, timeoutMs, pollMs, opts.onLog, opts.signal);
}

function abortError(): Error {
  const error = new Error("Operation aborted");
  error.name = "AbortError";
  return error;
}

function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (!signal) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

async function pollForMarkers(
  markerPath: string,
  failedPath: string,
  timeoutMs: number,
  pollMs: number,
  onLog?: (msg: string) => void,
  signal?: AbortSignal,
): Promise<GitReadyState> {
  const deadline = Date.now() + timeoutMs;
  // Loop: check ready first, then failed, then deadline. Checking ready first
  // means that if the background writer somehow set both (it never should), the
  // benign "ready" wins over the destructive "failed".
  for (;;) {
    if (signal?.aborted) throw abortError();
    if (await fileExists(markerPath)) {
      onLog?.("workspace git ready");
      return "ready";
    }
    if (await fileExists(failedPath)) {
      onLog?.("workspace git preparation failed (marker present)");
      return "failed";
    }
    if (Date.now() >= deadline) {
      onLog?.(`workspace git not ready after ${timeoutMs}ms — giving up`);
      return "timeout";
    }
    await abortableDelay(pollMs, signal);
  }
}
